/* eslint-disable security/detect-object-injection */
import { Host, h } from "@stencil/core";
/**
 * TODO: It could be great to render the navigation only on demand. At this time a solution should be found for the animation.
 */
let id = 0;
export class CoreTabsHorizontal {
    constructor() {
        this.tabsId = `tabs-horizontal-${id++}`;
        this.activeTabIndex = 0;
        this.stretch = false;
        this.iconPosition = 'left';
        this.nextButtonLabel = 'Suivant';
        this.previousButtonLabel = 'Précédent';
        this.internalActiveTabIndex = 0;
        this.internalFocusedTabIndex = 0;
        this.tabRefs = [];
        this.tabHeaderRefs = [];
        this.tabObjects = [];
        this.overflow = false;
        this.isDisplayingButtons = false;
    }
    componentWillLoad() {
        if (this.activeTabIndex)
            this.internalActiveTabIndex = this.activeTabIndex;
        if (this.internalActiveTabIndex !== this.activeTabIndex) {
            this.coreTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: this.activeTabIndex });
        }
        this.internalFocusedTabIndex = this.internalActiveTabIndex;
        this.onSlotChange();
    }
    componentDidLoad() {
        this.setOverflowState();
        this.isDisplayingButtons = this.overflow && !this.stretch;
        this.handleNavigation(this.internalActiveTabIndex);
        // Add keyboard event listeners for each tab header
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.addEventListener('focus', this.onTabFocus(index));
        });
        document.addEventListener('click', event => {
            if (!event.composedPath().includes(this.element)) {
                this.updateTabFocusability();
            }
        });
    }
    disconnectedCallback() {
        // Remove keyboard event listeners when component is unmounted
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.removeEventListener('focus', this.onTabFocus(index));
        });
    }
    componentDidUpdate() {
        this.updateTabFocusability();
    }
    handleNavigationOnResize() {
        this.setOverflowState();
        this.isDisplayingButtons = this.overflow && !this.stretch;
        this.handleNavigation(this.internalActiveTabIndex);
    }
    handleNavigation(index) {
        const prevButton = this.element.querySelector('.previous-button');
        const nextButton = this.element.querySelector('.next-button');
        // adjust button animation depending on index
        // the display property is added or removed asynchronously from the width and opacity to allow CSS animation
        if (index > 0 && this.isDisplayingButtons) {
            // @ts-expect-error
            prevButton.style.display = 'block';
            setTimeout(() => {
                prevButton.classList.remove('previous-button--hidden');
            }, 100);
        }
        else if (index === 0 || !this.isDisplayingButtons) {
            prevButton.classList.add('previous-button--hidden');
            setTimeout(() => {
                // @ts-expect-error
                prevButton.style.display = 'none';
            }, 100);
        }
        if (index < this.tabObjects.length - 1 && this.isDisplayingButtons) {
            // @ts-expect-error
            nextButton.style.display = 'block';
            setTimeout(() => {
                nextButton.classList.remove('next-button--hidden');
            }, 100);
        }
        else if (index === this.tabObjects.length - 1 || !this.isDisplayingButtons) {
            nextButton.classList.add('next-button--hidden');
            setTimeout(() => {
                // @ts-expect-error
                nextButton.style.display = 'none';
            }, 100);
        }
    }
    setActiveAndFocusedTab(index) {
        this.internalActiveTabIndex = index;
        this.internalFocusedTabIndex = index;
    }
    activeTabIndexChanged(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.setActiveAndFocusedTab(newValue);
        }
    }
    // when a slot is removed / added
    onSlotChange() {
        const tabs = this.element.querySelectorAll('core-tab-panel');
        this.tabObjects = Array.from(tabs).map(tab => {
            const pillNumber = tab.getAttribute('notification-pill-number') !== '' ? tab.getAttribute('notification-pill-number') : 'undefined';
            return {
                label: tab.getAttribute('label'),
                iconName: tab.getAttribute('icon-name'),
                notificationPillNumber: pillNumber,
                disabled: tab.getAttribute('disabled') === 'true'
            };
        });
        // Ensure icons have the right variant
        this.setIconDependingOnSelectedTab();
        this.tabRefs = Array.from(tabs);
        this.tabRefs.forEach((tab, index) => {
            tab.setAttribute('slot', `${this.tabsId}_tab-${index}`);
        });
    }
    setIconDependingOnSelectedTab() {
        this.tabObjects.forEach((tabPanel, index) => {
            if (!tabPanel.iconName)
                return;
            if (index !== this.internalActiveTabIndex || this.tabObjects[index].disabled) {
                this.tabObjects[index].iconName = tabPanel.iconName.replace('-solid', '-outline');
            }
            else {
                this.tabObjects[index].iconName = tabPanel.iconName.replace('-outline', '-solid');
            }
        });
    }
    setOverflowState() {
        const coreTabsList = this.element.querySelector('.core-tabs-list');
        if (!coreTabsList)
            return;
        this.overflow = coreTabsList.scrollWidth > coreTabsList.clientWidth;
    }
    onTabFocus(index) {
        return () => {
            this.internalFocusedTabIndex = index;
        };
    }
    updateTabFocusability() {
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.tabIndex = index === this.internalActiveTabIndex ? 0 : -1;
        });
    }
    focusNextTab() {
        let nextIndex = this.internalFocusedTabIndex + 1;
        while (nextIndex < this.tabHeaderRefs.length && this.tabObjects[nextIndex].disabled) {
            nextIndex++;
        }
        if (nextIndex >= 0 && nextIndex < this.tabHeaderRefs.length) {
            this.internalFocusedTabIndex = nextIndex;
            this.tabHeaderRefs[nextIndex].focus();
            this.tabHeaderRefs[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
        this.handleNavigation(nextIndex);
    }
    focusPreviousTab() {
        let prevIndex = this.internalFocusedTabIndex - 1;
        while (prevIndex >= 0 && this.tabObjects[prevIndex].disabled) {
            prevIndex--;
        }
        if (prevIndex >= 0 && prevIndex < this.tabHeaderRefs.length) {
            this.internalFocusedTabIndex = prevIndex;
            this.tabHeaderRefs[prevIndex].focus();
            this.tabHeaderRefs[prevIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
        this.handleNavigation(prevIndex);
    }
    getTabItemClass(index) {
        const isActive = index === this.internalActiveTabIndex && !this.tabObjects[index].disabled;
        const isDisabled = this.tabObjects[index].disabled;
        return `core-tab-item ${isActive ? 'tab-item--selected' : ''} ${isDisabled ? 'tab-item--disabled' : ''} ${this.stretch ? 'tab-item--stretch' : ''} tab-item--icon-${this.iconPosition}`;
    }
    handleClick(tab, index) {
        if (tab.disabled)
            return;
        this.coreTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: index });
        this.internalActiveTabIndex = index;
        this.internalFocusedTabIndex = index;
        // Ensure to get back to original icon
        this.setIconDependingOnSelectedTab();
    }
    handleKeyDown(event) {
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                // Shift + Tab
                if (this.internalFocusedTabIndex === 0) {
                    // Allow default behavior to move focus out of component
                }
                else {
                    event.preventDefault();
                    this.focusPreviousTab();
                }
            }
            else {
                // Tab
                if (this.internalFocusedTabIndex === this.tabHeaderRefs.length - 1) {
                    // Allow default behavior to move focus out of component
                }
                else {
                    event.preventDefault();
                    this.focusNextTab();
                }
            }
        }
        else if (event.key === 'Enter' && this.internalFocusedTabIndex !== -1 && !this.tabObjects[this.internalFocusedTabIndex].disabled) {
            const previouslyActiveTabIndex = this.internalActiveTabIndex;
            this.coreTabChange.emit({ previousTab: previouslyActiveTabIndex, currentTab: this.internalFocusedTabIndex });
            this.internalActiveTabIndex = this.internalFocusedTabIndex;
            // Ensure to get back to original icon
            this.setIconDependingOnSelectedTab();
        }
    }
    render() {
        return (h(Host, { key: 'dd1251665fccd10e5e0d27b629217604b815032e' }, h("div", { key: 'b6d06acf79fb7c01bea3c092cb195db3ccd84604', id: this.tabsId, class: "core-tabs core-tabs--horizontal" }, h("div", { key: 'e8fc37baf784b0fe92086bffe7c1794f48bf918b', class: "list-header-wrapper" }, h("core-icon-button", { key: 'f90836fe48beb3199adcdfb0e4ecd5edce76e365', class: "previous-button previous-button--hidden", label: this.previousButtonLabel, iconName: "chevron_left-outline", variant: "quaternary", onCoreIconButtonClick: () => {
                this.focusPreviousTab();
            }, "aria-hidden": !this.isDisplayingButtons }), h("div", { key: 'f6f1b01fa0a4d5e47988b613769cfe0b693f88ba', class: "core-tabs-list-header" }, h("ul", { key: '2de1f6855fe5c3c3da7d37a3409ffca838525cf2', role: "tablist", class: `core-tabs-list ${this.iconPosition === 'top' ? 'typography-monoline-xsmall' : 'typography-monoline-medium'}` }, this.tabObjects.map((tab, index) => (h("li", { id: `tab-label-${index}`, class: this.getTabItemClass(index), ref: el => (this.tabHeaderRefs[index] = el), tabindex: "0", onClick: () => {
                this.handleClick(tab, index);
            }, "aria-selected": index === this.internalActiveTabIndex ? 'true' : 'false', "aria-disabled": tab.disabled ? 'true' : 'false', role: "tab", "aria-controls": `${this.tabsId}_tab-panel-${index}` }, h("div", { class: "tab-label-content" }, tab.iconName && (h("div", { class: "icon-box" }, tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && this.iconPosition === 'top' && (h("core-notification-pill", { number: tab.notificationPillNumber })), h("core-icon", { name: tab.iconName, size: this.iconPosition === 'top' ? 'number-size-icon-large' : 'number-size-icon-medium' }))), h("span", { class: "tab-label" }, tab.label), tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && this.iconPosition === 'left' && (h("core-notification-pill", { number: tab.notificationPillNumber })))))))), h("core-icon-button", { key: 'e6c4d01c02251ba3ad73e66efd138350d87cf525', class: "next-button next-button--hidden", label: this.nextButtonLabel, iconName: "chevron_right-outline", variant: "quaternary", onCoreIconButtonClick: () => {
                this.focusNextTab();
            }, "aria-hidden": !this.isDisplayingButtons })), h("div", { key: '69c06999a1378824f4ecb175981b82fa4bc78e2d', class: "tab-content" }, Array.from(this.tabObjects).map((_, index) => (h("div", { id: `${this.tabsId}_tab-panel-${index}`, role: "tabpanel", "aria-labelledby": `tab-label-${index}`, style: { display: index === this.internalActiveTabIndex && !this.tabObjects[index].disabled ? 'block' : 'none' } }, h("slot", { name: `${this.tabsId}_tab-${index}` }))))))));
    }
    static get is() { return "core-tabs-horizontal"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-tabs-horizontal.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-tabs-horizontal.css"]
        };
    }
    static get properties() {
        return {
            "activeTabIndex": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set a tab as active by providing the index (tab can not be disabled)."
                },
                "attribute": "active-tab-index",
                "reflect": false,
                "defaultValue": "0"
            },
            "stretch": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Force the tab labels to have the same size and take the full width of the tabs component."
                },
                "attribute": "stretch",
                "reflect": false,
                "defaultValue": "false"
            },
            "iconPosition": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'top' | 'left'",
                    "resolved": "\"left\" | \"top\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Change the position of the icon for all tabs."
                },
                "attribute": "icon-position",
                "reflect": false,
                "defaultValue": "'left'"
            },
            "nextButtonLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label of the next button."
                },
                "attribute": "next-button-label",
                "reflect": false,
                "defaultValue": "'Suivant'"
            },
            "previousButtonLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label of the previous button."
                },
                "attribute": "previous-button-label",
                "reflect": false,
                "defaultValue": "'Pr\u00E9c\u00E9dent'"
            }
        };
    }
    static get states() {
        return {
            "internalActiveTabIndex": {},
            "internalFocusedTabIndex": {},
            "tabRefs": {},
            "tabHeaderRefs": {},
            "tabObjects": {},
            "overflow": {},
            "isDisplayingButtons": {}
        };
    }
    static get events() {
        return [{
                "method": "coreTabChange",
                "name": "coreTabChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "activeTabIndex",
                "methodName": "activeTabIndexChanged"
            }];
    }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "handleNavigationOnResize",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "slotchange",
                "method": "onSlotChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=core-tabs-horizontal.js.map
